import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import type {Button, AriaMenuItem} from '@pexip/components';
import {AriaMenu, Color, Icon, IconTypes} from '@pexip/components';

import type {InMeetingParticipant, ParticipantID} from '../../types';
import {TestId} from '../../../test/testIds';
import {ParticipantRowMoreActionsTouchModal} from '../ParticipantRowMoreActionsTouchModal/ParticipantRowMoreActionsTouchModal.view';
import {ParticipantRowMoreActionsHeader} from '../ParticipantRowMoreActionsHeader/ParticipantRowMoreActionsHeader.view';
import {MAIN_ROOM_ID} from '@pexip/infinity';

export const ParticipantRowMoreActions: React.FC<{
    isOpen: boolean;
    isTouchDevice: boolean;
    showParticipantAuthenticatedState: boolean;
    displayName: string;
    handleTransfer?: (identity: ParticipantID) => void;
    handleDTMF?: (identity: ParticipantID) => void;
    handleFecc?: (identity: ParticipantID) => void;
    handleRemoveUserRequest: (
        participant: InMeetingParticipant,
        displayName: string,
    ) => void;
    shouldShowHostActions: boolean;
    shouldShowHostActionsForCurrentPeer: boolean;
    isMuted: boolean;
    isDirectMedia?: boolean;
    isCurrentPeer: boolean;
    isSpotlight?: boolean;
    isSpotlightAvailable?: boolean;
    participant: InMeetingParticipant;
    additionalMenuContent: AriaMenuItem[];
    button: React.ReactElement<typeof Button>;
    onOpenChange: (state: boolean) => void;
}> = ({
    isOpen,
    isTouchDevice,
    showParticipantAuthenticatedState,
    displayName,
    handleTransfer,
    handleDTMF,
    handleFecc,
    handleRemoveUserRequest,
    shouldShowHostActions,
    shouldShowHostActionsForCurrentPeer,
    isMuted,
    isDirectMedia,
    isCurrentPeer,
    isSpotlight,
    isSpotlightAvailable,
    participant,
    additionalMenuContent,
    button,
    onOpenChange,
}) => {
    const {t} = useTranslation();

    const menuContent: AriaMenuItem[] = useMemo(() => {
        return [
            Boolean(participant.lowerHand) &&
                !isCurrentPeer &&
                participant.canRaiseHand &&
                participant.raisedHand && {
                    label: t('meeting.lower-hand', 'Lower hand'),
                    onItemClick: () => participant.lowerHand?.(),
                    testId: TestId.ActionLowerHand,
                    key: 'lowerHand',
                    closeMenuOnItemClick: false,
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={IconTypes.IconLowerhand}
                        />
                    ),
                },
            (shouldShowHostActions ||
                (isMuted && shouldShowHostActionsForCurrentPeer)) &&
                participant.canMute && {
                    label: isMuted
                        ? t('media.unmute-microphone', 'Unmute microphone')
                        : t('media.mute-microphone', 'Mute microphone'),
                    onItemClick: participant.mute,
                    testId: isMuted
                        ? TestId.ActionUnmuteMicrophone
                        : TestId.ActionMuteMicrophone,
                    key: 'microphone',
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={
                                isMuted
                                    ? IconTypes.IconMicrophoneOff
                                    : IconTypes.IconMicrophoneOn
                            }
                        />
                    ),
                },
            !isDirectMedia &&
                isSpotlightAvailable &&
                Boolean(participant.spotlight) &&
                participant.canSpotlight && {
                    label: isSpotlight
                        ? t('media.remove-spotlight', 'Remove spotlight')
                        : t('media.spotlight', 'Spotlight'),
                    onItemClick: () => participant.spotlight?.(),
                    testId: isSpotlight
                        ? TestId.ActionRemoveSpotlight
                        : TestId.ActionSpotlight,
                    closeMenuOnItemClick: false,
                    key: 'spotlight',
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={IconTypes.IconSpotlight}
                        />
                    ),
                },
            Boolean(participant.setRole && !isCurrentPeer) &&
                !participant.isExternal &&
                participant.roomId === MAIN_ROOM_ID && {
                    label: participant.isHost
                        ? t('meeting.make-guest', 'Make guest')
                        : t('meeting.make-host', 'Make host'),
                    onItemClick: () => participant.setRole?.(),
                    testId: participant.isHost
                        ? TestId.ActionMakeGuest
                        : TestId.ActionMakeHost,
                    key: 'setRole',
                    closeMenuOnItemClick: false,
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={
                                participant.isHost
                                    ? IconTypes.IconGuest
                                    : IconTypes.IconHost
                            }
                        />
                    ),
                },
            Boolean(handleTransfer) &&
                participant.canTransfer && {
                    label: t('media.transfer', 'Transfer'),
                    onItemClick: () => handleTransfer?.(participant.uuid),
                    testId: TestId.ActionTransfer,
                    key: 'transfer',
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={IconTypes.IconArrowCornerDown}
                        />
                    ),
                },
            !isDirectMedia &&
                Boolean(handleDTMF) &&
                !participant.isConnecting && {
                    label: t('dtmf.enter-dtmf', 'Enter DTMF'),
                    onItemClick: () => handleDTMF?.(participant.uuid),
                    testId: TestId.ActionEnterDTMF,
                    key: 'dtmf',
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={IconTypes.IconDtmf}
                        />
                    ),
                },
            Boolean(handleFecc) &&
                participant.canFecc && {
                    label: t('fecc.camera-control', 'Camera control'),
                    onItemClick: () => handleFecc?.(participant.uuid),
                    testId: TestId.ActionFecc,
                    key: 'fecc',
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={IconTypes.IconFecc}
                        />
                    ),
                },

            shouldShowHostActions &&
                participant.canDisconnect && {
                    label: t('meeting.disconnect', 'Disconnect'),
                    onItemClick: () =>
                        handleRemoveUserRequest(participant, displayName),
                    testId: TestId.ButtonKickOffParticipant,
                    key: 'disconnect',
                    icon: (
                        <Icon
                            size={isTouchDevice ? 'small' : 'compact'}
                            source={IconTypes.IconPhoneHorisontal}
                        />
                    ),
                    style: {color: Color.Red70},
                },
            ...additionalMenuContent.map(item => ({
                label: item.label,
                onItemClick: item.onItemClick,
                key: item.key,
                icon: item.icon,
                isActive: item.isActive,
            })),
        ].filter(Boolean) as AriaMenuItem[];
    }, [
        isDirectMedia,
        participant,
        isSpotlight,
        isSpotlightAvailable,
        t,
        handleTransfer,
        isCurrentPeer,
        handleDTMF,
        handleFecc,
        additionalMenuContent,
        displayName,
        handleRemoveUserRequest,
        shouldShowHostActions,
        shouldShowHostActionsForCurrentPeer,
        isMuted,
        isTouchDevice,
    ]);

    return isTouchDevice ? (
        <ParticipantRowMoreActionsTouchModal
            showParticipantAuthenticatedState={
                showParticipantAuthenticatedState
            }
            isAuthenticated={!!participant.isIdpAuthenticated}
            displayName={displayName}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            menuContent={menuContent}
            button={button}
        />
    ) : (
        <AriaMenu
            headerContent={
                <ParticipantRowMoreActionsHeader
                    isTouchDevice={false}
                    className="pt-4 pl-4 pr-4 pb-1"
                    displayName={displayName}
                    isAuthenticated={!!participant.isIdpAuthenticated}
                    showParticipantAuthenticatedState={
                        showParticipantAuthenticatedState
                    }
                />
            }
            onOpenChange={onOpenChange}
            button={button}
            items={menuContent}
            testId="kebab-menu"
        />
    );
};
